import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Plus, Link2, Building2, User, CircleUser, Trash2, CheckCircle2, Sparkle, Loader2, RefreshCcw, AlertCircle, ChevronDown } from 'lucide-react'
import { PageShell } from '../components/layout/PageShell'
import { PageHeader } from '../components/layout/PageHeader'
import { OwnershipCounter } from '../components/fields/OwnershipCounter'
import { FormField } from '../components/fields/FormField'
import { AutoFillField } from '../components/fields/AutoFillField'
import { inputClassName } from '../components/fields/fieldStyles'
import { Button } from '../components/ui/Button'
import { SectionTopAlert } from '../components/ui/SectionTopAlert'
import { StartOverButton } from '../components/ui/StartOverButton'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { BackButton } from '../components/ui/BackButton'
import { SaveIndicator } from '../components/ui/SaveIndicator'
import { Badge } from '../components/ui/Badge'
import { Checkbox } from '../components/ui/Checkbox'
import { UploadZone } from '../components/upload/UploadZone'
import { ProcessingCard } from '../components/upload/ProcessingCard'
import { DocumentCard } from '../components/upload/DocumentCard'
import { useFormStore } from '../store/formStore'
import { scrollToFirstError, FIRST_INVALID_FIELD } from '../lib/scrollToError'
import { useSession } from '../apply/session'
import type { DocumentSummary, UploadedFileInfo } from '../api/types'
import {
  processDocument,
  type ConfidenceMap,
  type DocAIExtraction,
  type ExtractedDirector,
  type ExtractedShareholder,
  type UploadSlot,
} from '../api/docai'

// ─── Types ──────────────────────────────────────────────────────────────────

interface PersonOwner {
  kind: 'person'
  id: string
  firstName: string
  lastName: string
  ownershipPct: number
  nationality: string
  dob: string
  passportNumber: string
  placeOfIssue: string
  registeredAddress: string
  isAutoFilled: boolean
  // MAF 26-05: per-field AI confidence; a key is removed once that field is edited.
  confidenceByField: ConfidenceMap
}

interface HoldingOwner {
  kind: 'holding'
  id: string
  legalCompanyName: string
  ownershipPct: number
  countryOfIncorporation: string
  registrationNumber: string
  isAutoFilled: boolean
  confidenceByField: ConfidenceMap
}

type Owner = PersonOwner | HoldingOwner

interface DirectorEntry {
  id: string
  firstName: string
  lastName: string
  dob: string
  nationality: string
  passportNumber: string
  placeOfIssue: string
  isAutoFilled: boolean
  linkedOwnerName?: string
  isAuthorisedSignatory: boolean
  confidenceByField: ConfidenceMap
}

/** Pre-split shape used by drafts saved before directors had firstName/lastName. */
interface LegacyDirectorEntry extends Omit<DirectorEntry, 'firstName' | 'lastName'> {
  name?: string
  firstName?: string
  lastName?: string
}

/** One-time upgrade for drafts persisted under the old single "name" field
 *  (pre firstName/lastName split); leaves already-migrated entries untouched. */
function migrateDirectorEntry(d: LegacyDirectorEntry): DirectorEntry {
  if (d.firstName !== undefined || d.lastName !== undefined) {
    return { ...d, firstName: d.firstName ?? '', lastName: d.lastName ?? '' }
  }
  const [firstName, ...rest] = (d.name ?? '').trim().split(/\s+/)
  return { ...d, firstName: d.name ? firstName : '', lastName: rest.join(' ') }
}

type SlotState = 'idle' | 'processing' | 'done' | 'error'

// ─── Copy constants ─────────────────────────────────────────────────────────

const UPLOAD_SUBTEXT = 'JPEG, PNG, PDF · Max 10MB · Max 50 pages (PDF) · 🔒 Encrypted & Private'

const UPLOAD_ERROR_TEXT =
  "We couldn't read this document. Try uploading it again, or fill in the fields manually."

// ─── Helpers ─────────────────────────────────────────────────────────────────

function personFullName(o: PersonOwner): string {
  return `${o.firstName} ${o.lastName}`.trim()
}

function directorFullName(d: Pick<DirectorEntry, 'firstName' | 'lastName'>): string {
  return `${d.firstName} ${d.lastName}`.trim()
}

/** True when a document already exists for this (slot, slotIndex) — seeds
 *  CompactUploadSlot as 'done' on mount instead of forcing a re-upload of a
 *  file that's already persisted (e.g. navigating back via Review's "Edit"). */
function hasDocument(documents: DocumentSummary[], slot: UploadSlot, slotIndex: number): boolean {
  return documents.some((d) => d.slot === slot && d.slot_index === slotIndex)
}

function computeLinkedOwner(directorName: string, owners: Owner[]): string | undefined {
  const normalized = directorName.trim().toLowerCase()
  if (!normalized) return undefined
  const persons = owners.filter((o): o is PersonOwner => o.kind === 'person')
  const match = persons.find((o) => personFullName(o).toLowerCase() === normalized)
  return match ? personFullName(match) : undefined
}

// ─── Compact upload slot used inside person cards ──────────────────────────

interface CompactUploadSlotProps {
  label: string
  /** Backend document slot; drives the SF documentType and DB persistence. */
  slot: UploadSlot
  /** Card position, so re-uploads for the same person supersede each other. */
  slotIndex: number
  onComplete: (result: DocAIExtraction) => void
  successMessage: string
  /** Clears the fields this upload filled (and their AI marks). When provided,
   *  the done state shows a Delete action alongside Replace. */
  onRemove?: () => void
  /** True when this (slot, slotIndex) already has a persisted document — seeds
   *  the initial state as 'done' instead of 'idle' so navigating back to this
   *  step (Review's "Edit", or any remount) doesn't force a re-upload of a
   *  file that's already on the account. */
  alreadyUploaded?: boolean
}

function CompactUploadSlot({ label, slot, slotIndex, onComplete, successMessage, onRemove, alreadyUploaded }: CompactUploadSlotProps) {
  const [state, setState] = useState<SlotState>(alreadyUploaded ? 'done' : 'idle')

  async function handleFile(file: File) {
    setState('processing')
    try {
      const result = await processDocument(file, slot, slotIndex)
      onComplete(result)
      setState('done')
    } catch (err) {
      console.error(`[docai] ${slot} extraction failed`, err)
      setState('error')
    }
  }

  function reset() {
    setState('idle')
  }

  return (
    // 32px breathing room above and below this optional upload slot. The card's
    // field stack already has a 16px flex gap, so 16px of margin tops it up to 32.
    <div style={{ marginTop: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
      <p
        style={{
          margin: '0 0 var(--spacing-2) 0',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          flexWrap: 'wrap',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-md)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
        }}
      >
        {label}
        <Badge variant="optional" size="md">Optional</Badge>
      </p>
      {(state === 'idle' || state === 'error') && (
        <>
          <UploadZone
            onFileAccepted={handleFile}
            subtext={UPLOAD_SUBTEXT}
          />
          {state === 'error' && (
            <p
              role="alert"
              style={{
                margin: 'var(--spacing-2) 0 0 var(--spacing-3)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                color: 'var(--color-error-text)',
              }}
            >
              {UPLOAD_ERROR_TEXT}
            </p>
          )}
        </>
      )}
      {state === 'processing' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            background: 'var(--color-surface-container-lowest)',
            border: '1px solid var(--color-outline-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <Loader2
            size={20}
            aria-hidden="true"
            style={{ flexShrink: 0, animation: 'spin 0.7s linear infinite' }}
          />
          Extracting details...
        </div>
      )}
      {state === 'done' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            background: 'var(--color-surface-container-lowest)',
            border: '1px solid var(--color-outline-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
          }}
        >
          <CheckCircle2
            size={20}
            aria-hidden="true"
            style={{ color: 'var(--color-success-text)', flexShrink: 0 }}
          />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--color-text-primary)',
            }}
          >
            {successMessage}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexShrink: 0 }}>
            <button
              type="button"
              onClick={reset}
              aria-label={`Replace — ${successMessage}`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-label-sm)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--color-action-primary)',
              }}
            >
              <RefreshCcw size={16} strokeWidth={1.5} aria-hidden="true" />
              Replace
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={() => {
                  onRemove()
                  reset()
                }}
                aria-label={`Delete — ${successMessage}`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-label-sm)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--color-error-text)',
                }}
              >
                <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" />
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Auto-filled badge ──────────────────────────────────────────────────────

function AutoFilledBadge() {
  // Matches the canonical "AI-filled" pill on the auto-filled fields
  // (gradient + sparkle, weight 500, 4px gap, no glow) — keep in sync with
  // the badge in AutoFillField.tsx.
  return (
    <span
      aria-label="Auto-filled by AI"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        background: 'var(--gradient-action)',
        color: 'var(--field-autofill-badge-text)',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.6875rem',
        fontWeight: 'var(--weight-medium)',
        fontFamily: 'var(--font-body)',
        padding: '4px 9px',
        lineHeight: 1,
        letterSpacing: '0.02em',
      }}
    >
      <Sparkle size={13} fill="#ffffff" strokeWidth={1.5} aria-hidden="true" />
      AI-filled
    </span>
  )
}

// ─── Collapse toggle (shared chevron button for owner/director cards) ──────

function CollapseToggle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        flexShrink: 0,
        padding: 0,
        background: 'none',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
      }}
    >
      <ChevronDown
        size={18}
        aria-hidden="true"
        style={{
          color: 'var(--color-text-secondary)',
          transition: 'transform var(--duration-standard) var(--ease-out)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </button>
  )
}

// Collapsible-card title: the entry *type* ("Director 1") reads lighter so the
// actual *name* stands out. Tight line-height keeps a wrapped long name aligned
// with the leading-icon (which is centred on the first line via its marginTop).
function CardTitle({ type, name, badge }: { type: string; name: string; badge?: React.ReactNode }) {
  const base: React.CSSProperties = {
    fontFamily: 'var(--font-display)',
    // One step down the type scale from title-sm (18px) → 16px, the next
    // distinct size (there is no title-xs token).
    fontSize: 'var(--text-body-md)',
    lineHeight: 'var(--leading-tight)',
    color: 'var(--color-text-primary)',
  }
  // The badge follows the name separated by a plain SPACE — not a left margin,
  // and not glued to the last word. Both alternatives were built and measured:
  //   • margin-left: the badge drops to its own line carrying the 8px margin,
  //     so it reads as a stray indent instead of a deliberate gap.
  //   • gluing the last word to the badge: it stops the orphan, but the word
  //     and badge become one long unbreakable unit, so a name that would have
  //     fitted wraps early — "Washim TOORABALLY" splits after "Washim" and
  //     leaves line one nearly empty. Worse than the problem it solved.
  // A space collapses at a line start, so the badge lands flush under the name
  // when it wraps, and the name is never split. Measured at the widths where
  // each case bites.
  return (
    <>
      <span style={{ ...base, fontWeight: 'var(--weight-semibold)' }}>
        {type}
        {name ? ': ' : ''}
      </span>
      {name && <span style={{ ...base, fontWeight: 'var(--weight-regular)' }}>{name}</span>}
      {badge && (
        <>
          {/* The separator is a real space, widened with word-spacing rather
              than replaced by a margin. A margin survives a line break and
              lands the badge indented; a space is dropped at a line start, so
              the badge sits flush. word-spacing adds to the space's own width,
              so it disappears with it. 4px on top of the ~4.3px space puts the
              visible gap on 8px (spacing-2). */}
          <span style={{ wordSpacing: 'var(--spacing-1)' }}> </span>
          <span style={{ display: 'inline-flex', verticalAlign: 'middle', position: 'relative', top: '-1px' }}>
            {badge}
          </span>
        </>
      )}
    </>
  )
}

// ─── Shareholder Card (person) ──────────────────────────────────────────────

interface ShareholderCardProps {
  owner: PersonOwner
  index: number
  onChange: (id: string, field: keyof PersonOwner, value: string | number) => void
  onPassportFill: (id: string, result: DocAIExtraction) => void
  onProofOfAddressFill: (id: string, result: DocAIExtraction) => void
  /** Clear the fields a passport / proof-of-address upload filled (Delete). */
  onPassportClear: (id: string) => void
  onProofOfAddressClear: (id: string) => void
  /** Play the fill shimmer on the fields the matching upload just filled. */
  passportShimmer: boolean
  addressShimmer: boolean
  onRemove: (id: string) => void
  /** After a blocked Continue, flag this card's empty required fields red. */
  showErrors: boolean
  /** Collapsed cards hide their field stack behind a summary header. */
  open: boolean
  onToggleOpen: () => void
  /** Space below this card in the stack (bigger when open or last). */
  bottomGap: string
  /** Server-persisted document summaries, for seeding the passport / proof-of-
   *  address upload slots as already-done on mount. */
  documents: DocumentSummary[]
}

function ShareholderCard({
  owner,
  index,
  onChange,
  onPassportFill,
  onProofOfAddressFill,
  onPassportClear,
  onProofOfAddressClear,
  passportShimmer,
  addressShimmer,
  onRemove,
  showErrors,
  open,
  onToggleOpen,
  bottomGap,
  documents,
}: ShareholderCardProps) {
  return (
    <motion.div
      key={owner.id}
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: [0, 0, 0.2, 1] }}
      style={{
        background: 'var(--color-surface-container-lowest)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-ambient)',
        // Clip to the rounded corner: without this the body can spill past the
        // radius for a frame while the card expands or collapses.
        overflow: 'hidden',
        padding: 'var(--spacing-8)',
        marginBottom: bottomGap,
        display: 'flex',
        flexDirection: 'column',
        // The 32px below the header lives INSIDE the collapsing body (as its
        // top padding), not here as a gap/margin that flips with `open`. A
        // toggling separator changes in one frame while the body is still
        // animating its height down, so the first field label jumps up 32px
        // and then vanishes. Inside the body it collapses together with it.
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--spacing-2)',
        }}
      >
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={open}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-2)',
            flex: 1,
            minWidth: 0,
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <CircleUser size={20} strokeWidth={2} aria-hidden="true" style={{ color: 'var(--color-action-primary)', flexShrink: 0, marginTop: '1px' }} />
          <span style={{ display: 'block', flex: 1, minWidth: 0, paddingRight: 'var(--spacing-4)' }}>
            <CardTitle type={`Individual Owner ${index + 1}`} name={personFullName(owner)} badge={owner.isAutoFilled && <AutoFilledBadge />} />
          </span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexShrink: 0 }}>
          <Button
            variant="danger-text"
            onClick={() => onRemove(owner.id)}
            aria-label={`Remove individual owner ${index + 1}`}
            style={{ fontSize: '0.8125rem', flexShrink: 0 }}
          >
            <Trash2 size={16} strokeWidth={2.5} aria-hidden="true" />
            Remove
          </Button>
          <CollapseToggle open={open} onClick={onToggleOpen} label={`individual owner ${index + 1}`} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-form-field-gap)', paddingTop: 'var(--spacing-8)' }}>
        <FormField label="First name" name={`owner-firstname-${owner.id}`} required error={showErrors && !owner.firstName.trim() ? 'First name is required' : undefined} confidence={owner.confidenceByField.firstName}>
          <input
            id={`owner-firstname-${owner.id}`}
            type="text"
            value={owner.firstName}
            onChange={(e) => onChange(owner.id, 'firstName', e.target.value)}
            placeholder="First name"
            className={inputClassName}
            aria-invalid={showErrors && !owner.firstName.trim() ? 'true' : undefined}
          />
        </FormField>

        <FormField label="Last name" name={`owner-lastname-${owner.id}`} required error={showErrors && !owner.lastName.trim() ? 'Last name is required' : undefined} confidence={owner.confidenceByField.lastName}>
          <input
            id={`owner-lastname-${owner.id}`}
            type="text"
            value={owner.lastName}
            onChange={(e) => onChange(owner.id, 'lastName', e.target.value)}
            placeholder="Last name"
            className={inputClassName}
            aria-invalid={showErrors && !owner.lastName.trim() ? 'true' : undefined}
          />
        </FormField>

        <FormField label="Ownership %" name={`owner-pct-${owner.id}`} required error={showErrors && !(owner.ownershipPct > 0) ? 'Ownership % is required' : undefined} confidence={owner.confidenceByField.ownershipPct}>
          <input
            id={`owner-pct-${owner.id}`}
            type="number"
            min={0}
            max={100}
            step={1}
            value={owner.ownershipPct || ''}
            onChange={(e) => onChange(owner.id, 'ownershipPct', parseFloat(e.target.value) || 0)}
            placeholder="e.g. 25"
            className={inputClassName}
            aria-invalid={showErrors && !(owner.ownershipPct > 0) ? 'true' : undefined}
          />
        </FormField>

        <CompactUploadSlot
          label="Valid passport copy"
          slot="shareholder_passport"
          slotIndex={index}
          onComplete={(result) => onPassportFill(owner.id, result)}
          onRemove={() => onPassportClear(owner.id)}
          successMessage="Passport details filled"
          alreadyUploaded={hasDocument(documents, 'shareholder_passport', index)}
        />

        <AutoFillField
          label="Passport number"
          name={`owner-passport-${owner.id}`}
          type="text"
          value={owner.passportNumber}
          placeholder="Passport number"
          isAutoFilled={owner.confidenceByField.passportNumber != null}
          triggerShimmer={passportShimmer}
          shimmerDelay={0}
          confidence={owner.confidenceByField.passportNumber}
          onChange={(val) => onChange(owner.id, 'passportNumber', val)}
        />

        <AutoFillField
          label="Nationality"
          name={`owner-nationality-${owner.id}`}
          type="text"
          value={owner.nationality}
          placeholder="Nationality"
          isAutoFilled={owner.confidenceByField.nationality != null}
          triggerShimmer={passportShimmer}
          shimmerDelay={120}
          confidence={owner.confidenceByField.nationality}
          onChange={(val) => onChange(owner.id, 'nationality', val)}
        />

        <FormField label="Date of birth" name={`owner-dob-${owner.id}`} confidence={owner.confidenceByField.dob}>
          <input
            id={`owner-dob-${owner.id}`}
            type="date"
            value={owner.dob}
            onChange={(e) => onChange(owner.id, 'dob', e.target.value)}
            placeholder="dd.mm.yyyy"
            className={inputClassName}
          />
        </FormField>

        <AutoFillField
          label="Place of issue"
          name={`owner-placeofissue-${owner.id}`}
          type="text"
          value={owner.placeOfIssue}
          placeholder="Place of issue"
          isAutoFilled={owner.confidenceByField.placeOfIssue != null}
          triggerShimmer={passportShimmer}
          shimmerDelay={240}
          confidence={owner.confidenceByField.placeOfIssue}
          onChange={(val) => onChange(owner.id, 'placeOfIssue', val)}
        />

        <CompactUploadSlot
          label="Proof of address"
          slot="shareholder_proof_of_address"
          slotIndex={index}
          onComplete={(result) => onProofOfAddressFill(owner.id, result)}
          onRemove={() => onProofOfAddressClear(owner.id)}
          successMessage="Address details filled"
          alreadyUploaded={hasDocument(documents, 'shareholder_proof_of_address', index)}
        />

        <AutoFillField
          label="Registered address"
          name={`owner-address-${owner.id}`}
          type="text"
          value={owner.registeredAddress}
          placeholder="Full address"
          isAutoFilled={owner.confidenceByField.registeredAddress != null}
          triggerShimmer={addressShimmer}
          shimmerDelay={0}
          confidence={owner.confidenceByField.registeredAddress}
          onChange={(val) => onChange(owner.id, 'registeredAddress', val)}
        />
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ─── Holding Company Card (MAF 15.05: 4 fields only) ───────────────────────

interface HoldingCardProps {
  owner: HoldingOwner
  index: number
  onChange: (id: string, field: keyof HoldingOwner, value: string | number) => void
  onRemove: (id: string) => void
  /** After a blocked Continue, flag this card's empty required fields red. */
  showErrors: boolean
  /** Collapsed cards hide their field stack behind a summary header. */
  open: boolean
  onToggleOpen: () => void
  /** Space below this card in the stack (bigger when open or last). */
  bottomGap: string
}

function HoldingCard({ owner, index, onChange, onRemove, showErrors, open, onToggleOpen, bottomGap }: HoldingCardProps) {
  return (
    <motion.div
      key={owner.id}
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: [0, 0, 0.2, 1] }}
      style={{
        background: 'var(--color-surface-container-lowest)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-ambient)',
        // Clip to the rounded corner: without this the body can spill past the
        // radius for a frame while the card expands or collapses.
        overflow: 'hidden',
        padding: 'var(--spacing-8)',
        marginBottom: bottomGap,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--spacing-2)',
          // The 32px below the header lives INSIDE the collapsing body (as its
          // top padding), not here as a gap/margin that flips with `open`. A
          // toggling separator changes in one frame while the body is still
          // animating its height down, so the first field label jumps up 32px
          // and then vanishes. Inside the body it collapses together with it.
        }}
      >
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={open}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-2)',
            flex: 1,
            minWidth: 0,
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Building2 size={20} strokeWidth={2} aria-hidden="true" style={{ color: 'var(--color-action-primary)', flexShrink: 0, marginTop: '1px' }} />
          <span style={{ display: 'block', flex: 1, minWidth: 0, paddingRight: 'var(--spacing-4)' }}>
            <CardTitle type={`Holding Company ${index + 1}`} name={owner.legalCompanyName} badge={owner.isAutoFilled && <AutoFilledBadge />} />
          </span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexShrink: 0 }}>
          <Button
            variant="danger-text"
            onClick={() => onRemove(owner.id)}
            aria-label={`Remove holding company ${index + 1}`}
            style={{ fontSize: '0.8125rem', flexShrink: 0 }}
          >
            <Trash2 size={16} strokeWidth={2.5} aria-hidden="true" />
            Remove
          </Button>
          <CollapseToggle open={open} onClick={onToggleOpen} label={`holding company ${index + 1}`} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-form-field-gap)', paddingTop: 'var(--spacing-8)' }}>
      <FormField label="Legal company name" name={`holding-legal-${owner.id}`} required error={showErrors && !owner.legalCompanyName.trim() ? 'Legal company name is required' : undefined} confidence={owner.confidenceByField.legalCompanyName}>
        <input
          id={`holding-legal-${owner.id}`}
          type="text"
          value={owner.legalCompanyName}
          onChange={(e) => onChange(owner.id, 'legalCompanyName', e.target.value)}
          placeholder="Full legal name"
          className={inputClassName}
          aria-invalid={showErrors && !owner.legalCompanyName.trim() ? 'true' : undefined}
        />
      </FormField>

      <FormField label="Ownership %" name={`holding-pct-${owner.id}`} required error={showErrors && !(owner.ownershipPct > 0) ? 'Ownership % is required' : undefined} confidence={owner.confidenceByField.ownershipPct}>
        <input
          id={`holding-pct-${owner.id}`}
          type="number"
          min={0}
          max={100}
          step={1}
          value={owner.ownershipPct || ''}
          onChange={(e) => onChange(owner.id, 'ownershipPct', parseFloat(e.target.value) || 0)}
          placeholder="e.g. 25"
          className={inputClassName}
          aria-invalid={showErrors && !(owner.ownershipPct > 0) ? 'true' : undefined}
        />
      </FormField>

      <FormField label="Country of Incorporation" name={`holding-country-${owner.id}`} required error={showErrors && !owner.countryOfIncorporation.trim() ? 'Country of Incorporation is required' : undefined} confidence={owner.confidenceByField.countryOfIncorporation}>
        <input
          id={`holding-country-${owner.id}`}
          type="text"
          value={owner.countryOfIncorporation}
          onChange={(e) => onChange(owner.id, 'countryOfIncorporation', e.target.value)}
          placeholder="Country"
          className={inputClassName}
          aria-invalid={showErrors && !owner.countryOfIncorporation.trim() ? 'true' : undefined}
        />
      </FormField>

      <FormField label="Registration number" name={`holding-regno-${owner.id}`} required error={showErrors && !owner.registrationNumber.trim() ? 'Registration number is required' : undefined} confidence={owner.confidenceByField.registrationNumber}>
        <input
          id={`holding-regno-${owner.id}`}
          type="text"
          value={owner.registrationNumber}
          onChange={(e) => onChange(owner.id, 'registrationNumber', e.target.value)}
          placeholder="Registration number"
          className={inputClassName}
          aria-invalid={showErrors && !owner.registrationNumber.trim() ? 'true' : undefined}
        />
      </FormField>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ─── Director Card (unchanged shape; passport-overrides-Incumbency wired) ──

interface DirectorCardProps {
  director: DirectorEntry
  index: number
  canRemove: boolean
  onChange: (id: string, field: string, value: string) => void
  onRemove: (id: string) => void
  onToggleSignatory: (id: string, checked: boolean) => void
  showSignatoryHelper: boolean
  /** Fill passport number + place of issue (and override nationality) from a
   *  passport scan; mirror of the shareholder card's passport upload. */
  onPassportFill: (id: string, result: DocAIExtraction) => void
  /** Clear the fields the passport upload filled (and their AI marks). */
  onPassportClear: (id: string) => void
  /** Play the fill shimmer on the fields the passport just filled. */
  passportShimmer: boolean
  /** After a blocked Continue, flag this card's empty required fields red. */
  showErrors: boolean
  /** Collapsed cards hide their field stack behind a summary header. */
  open: boolean
  onToggleOpen: () => void
  /** Space below this card in the stack (bigger when open or last). */
  bottomGap: string
  /** Server-persisted document summaries, for seeding the passport upload
   *  slot as already-done on mount. */
  documents: DocumentSummary[]
}

function DirectorCard({
  director,
  index,
  canRemove,
  onChange,
  onRemove,
  onToggleSignatory,
  showSignatoryHelper,
  onPassportFill,
  onPassportClear,
  passportShimmer,
  showErrors,
  open,
  onToggleOpen,
  bottomGap,
  documents,
}: DirectorCardProps) {
  return (
    <motion.div
      key={director.id}
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: [0, 0, 0.2, 1] }}
      style={{
        background: 'var(--color-surface-container-lowest)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-ambient)',
        // Clip to the rounded corner: without this the body can spill past the
        // radius for a frame while the card expands or collapses.
        overflow: 'hidden',
        padding: 'var(--spacing-8)',
        marginBottom: bottomGap,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--spacing-2)',
          // The 32px below the header lives INSIDE the collapsing body (as its
          // top padding), not here as a gap/margin that flips with `open`. A
          // toggling separator changes in one frame while the body is still
          // animating its height down, so the first field label jumps up 32px
          // and then vanishes. Inside the body it collapses together with it.
        }}
      >
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={open}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-2)',
            flex: 1,
            minWidth: 0,
            padding: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <CircleUser size={20} strokeWidth={2} aria-hidden="true" style={{ color: 'var(--color-action-primary)', flexShrink: 0, marginTop: '1px' }} />
          <span style={{ display: 'block', flex: 1, minWidth: 0, paddingRight: 'var(--spacing-4)' }}>
            <CardTitle type={`Director ${index + 1}`} name={directorFullName(director)} badge={director.isAutoFilled && <AutoFilledBadge />} />
          </span>
        </button>

        {/* 12px, same as the owner and holding cards. This row was on 4px with
            a wrap because the "Linked to" chip was squeezed in beside Remove;
            tightening one of three copies is what put the three cards out of
            step with each other. One gap for every collapsible card. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexShrink: 0 }}>
          <AnimatePresence>
            {director.linkedOwnerName && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-1)',
                  background: 'var(--color-action-primary-hover)',
                  color: 'var(--color-action-primary)',
                  borderRadius: 'var(--radius-full)',
                  padding: '2px 10px',
                  fontSize: 'var(--text-label-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  fontFamily: 'var(--font-body)',
                  whiteSpace: 'nowrap',
                }}
              >
                <Link2 size={12} aria-hidden="true" />
                Linked to {director.linkedOwnerName}
              </motion.span>
            )}
          </AnimatePresence>

          {canRemove && (
            <Button
              variant="danger-text"
              onClick={() => onRemove(director.id)}
              aria-label={`Remove director ${index + 1}`}
              style={{ fontSize: '0.8125rem' }}
            >
              <Trash2 size={16} strokeWidth={2.5} aria-hidden="true" />
              Remove
            </Button>
          )}
          <CollapseToggle open={open} onClick={onToggleOpen} label={`director ${index + 1}`} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-form-field-gap)', paddingTop: 'var(--spacing-8)' }}>
      <FormField label="First name" name={`director-firstname-${director.id}`} required error={showErrors && !director.firstName.trim() ? 'First name is required' : undefined} confidence={director.confidenceByField.firstName}>
        <input
          id={`director-firstname-${director.id}`}
          type="text"
          value={director.firstName}
          onChange={(e) => onChange(director.id, 'firstName', e.target.value)}
          placeholder="First name"
          className={inputClassName}
          aria-invalid={showErrors && !director.firstName.trim() ? 'true' : undefined}
        />
      </FormField>

      <FormField label="Last name" name={`director-lastname-${director.id}`} required error={showErrors && !director.lastName.trim() ? 'Last name is required' : undefined} confidence={director.confidenceByField.lastName}>
        <input
          id={`director-lastname-${director.id}`}
          type="text"
          value={director.lastName}
          onChange={(e) => onChange(director.id, 'lastName', e.target.value)}
          placeholder="Last name"
          className={inputClassName}
          aria-invalid={showErrors && !director.lastName.trim() ? 'true' : undefined}
        />
      </FormField>

      <CompactUploadSlot
        label="Valid passport copy"
        slot="director_passport"
        slotIndex={index}
        onComplete={(result) => onPassportFill(director.id, result)}
        onRemove={() => onPassportClear(director.id)}
        successMessage="Passport details filled"
        alreadyUploaded={hasDocument(documents, 'director_passport', index)}
      />

      <AutoFillField
        label="Passport number"
        name={`director-passport-${director.id}`}
        type="text"
        value={director.passportNumber}
        placeholder="Passport number"
        isAutoFilled={director.confidenceByField.passportNumber != null}
        triggerShimmer={passportShimmer}
        shimmerDelay={0}
        confidence={director.confidenceByField.passportNumber}
        onChange={(val) => onChange(director.id, 'passportNumber', val)}
      />

      <AutoFillField
        label="Nationality"
        name={`director-nationality-${director.id}`}
        type="text"
        value={director.nationality}
        placeholder="e.g. British"
        isAutoFilled={director.confidenceByField.nationality != null}
        triggerShimmer={passportShimmer}
        shimmerDelay={120}
        confidence={director.confidenceByField.nationality}
        onChange={(val) => onChange(director.id, 'nationality', val)}
      />

      <FormField label="Date of birth" name={`director-dob-${director.id}`} confidence={director.confidenceByField.dob}>
        <input
          id={`director-dob-${director.id}`}
          type="date"
          value={director.dob}
          onChange={(e) => onChange(director.id, 'dob', e.target.value)}
          className={inputClassName}
        />
      </FormField>

      <AutoFillField
        label="Place of issue"
        name={`director-placeofissue-${director.id}`}
        type="text"
        value={director.placeOfIssue}
        placeholder="Place of issue"
        isAutoFilled={director.confidenceByField.placeOfIssue != null}
        triggerShimmer={passportShimmer}
        shimmerDelay={240}
        confidence={director.confidenceByField.placeOfIssue}
        onChange={(val) => onChange(director.id, 'placeOfIssue', val)}
      />

      <div
        style={{
          // Per the design system, the signatory toggle is set off by white
          // space rather than a divider line. The card's field stack already
          // has a 16px gap; this 16px top padding tops it up to the documented
          // 32px (spacing-8) of separation.
          paddingTop: 'var(--spacing-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-2)',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
          <Checkbox
            checked={director.isAuthorisedSignatory}
            onChange={(checked) => onToggleSignatory(director.id, checked)}
          />
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-primary)',
            }}
          >
            This person is an authorised signatory for this application.
          </span>
        </label>
        {showSignatoryHelper && director.isAuthorisedSignatory && (
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-sm)',
              color: 'var(--color-text-secondary)',
              paddingLeft: 'var(--spacing-6)',
            }}
          >
            Only one authorised signatory can be designated.
          </p>
        )}
      </div>
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ─── Add-owner type picker ─────────────────────────────────────────────────

interface AddOwnerPickerProps {
  onPick: (kind: 'person' | 'holding') => void
  onCancel: () => void
}

function AddOwnerPicker({ onPick, onCancel }: AddOwnerPickerProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-6)',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-owner-title"
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-float)',
          padding: 'var(--spacing-6)',
          maxWidth: 420,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-4)',
        }}
      >
        <h3
          id="add-owner-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-sm)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
          }}
        >
          Add a shareholder or holding company
        </h3>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-secondary)',
          }}
        >
          Which type of owner would you like to add?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
          <button
            type="button"
            onClick={() => onPick('person')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-container-low)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-md)',
              color: 'var(--color-text-primary)',
            }}
          >
            <User size={20} aria-hidden="true" style={{ color: 'var(--color-action-primary)' }} />
            <div>
              <div style={{ fontWeight: 'var(--weight-semibold)' }}>Shareholder</div>
              <div style={{ fontSize: 'var(--text-label-sm)', color: 'var(--color-text-secondary)' }}>
                Individual person who owns shares
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => onPick('holding')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              padding: 'var(--spacing-4)',
              background: 'var(--color-surface-container-low)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-md)',
              color: 'var(--color-text-primary)',
            }}
          >
            <Building2 size={20} aria-hidden="true" style={{ color: 'var(--color-action-primary)' }} />
            <div>
              <div style={{ fontWeight: 'var(--weight-semibold)' }}>Holding company</div>
              <div style={{ fontSize: 'var(--text-label-sm)', color: 'var(--color-text-secondary)' }}>
                Corporate entity that owns shares
              </div>
            </div>
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="tertiary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </motion.div>
  )
}

// ─── Owners + directors from DocAI result (used after certificate upload) ──
// The incumbency template supplies names and ownership per shareholder, and
// names per director. Everything else (dob, nationality, passport, address;
// holding country/registration number) is NOT extracted — it stays empty for
// manual entry or the per-person passport/PoA uploads.

function ownersFromExtraction(shareholders: ExtractedShareholder[]): Owner[] {
  const people: PersonOwner[] = []
  const holdings: HoldingOwner[] = []
  shareholders.forEach((s, i) => {
    if (s.kind === 'person') {
      people.push({
        kind: 'person',
        id: `owner-p-${i}`,
        firstName: s.firstName,
        lastName: s.lastName,
        ownershipPct: s.ownershipPct ?? 0,
        nationality: '',
        dob: '',
        passportNumber: '',
        placeOfIssue: '',
        registeredAddress: '',
        isAutoFilled: true,
        confidenceByField: s.confidence,
      })
    } else {
      holdings.push({
        kind: 'holding',
        id: `owner-h-${i}`,
        legalCompanyName: s.companyName,
        ownershipPct: s.ownershipPct ?? 0,
        countryOfIncorporation: '',
        registrationNumber: '',
        isAutoFilled: true,
        confidenceByField: { ...s.confidence, legalCompanyName: s.confidence.companyName },
      })
    }
  })
  // People first, then holdings — extraction order preserved within groups.
  return [...people, ...holdings]
}

function directorsFromExtraction(extracted: ExtractedDirector[]): DirectorEntry[] {
  return extracted.map((d, i): DirectorEntry => {
    const confidenceByField: ConfidenceMap = {}
    if (d.confidence.firstName != null) confidenceByField.firstName = d.confidence.firstName
    if (d.confidence.lastName != null) confidenceByField.lastName = d.confidence.lastName
    return {
      id: `director-${i}`,
      firstName: d.firstName,
      lastName: d.lastName,
      dob: '',
      nationality: '',
      passportNumber: '',
      placeOfIssue: '',
      isAutoFilled: true,
      isAuthorisedSignatory: false,
      confidenceByField,
    }
  })
}

// ─── Ownership upload slot (idle → processing → done) ──────────────────────
// MAF 29-05: shared by the Incumbency lane and the Shareholders / Directors
// pair. Reuses the same ProcessingCard / DocumentCard as the rest of the flow.
interface OwnershipUploadSlotProps {
  state: SlotState
  file: UploadedFileInfo | null
  /** The in-flight extraction request (set alongside state 'processing'). */
  task: Promise<DocAIExtraction> | null
  onFile: (f: File) => void
  onComplete: (result: DocAIExtraction) => void
  onError: (err: unknown) => void
  onReset: () => void
  /** Real number of fields/rows the extraction returned; drives the card's
   *  "AI extracted …" line. Undefined (e.g. after a reload) hides it. */
  fieldCount?: number
  /** Highlight the drop zone red when this required upload is still missing. */
  requiredError?: boolean
}

// Shown under an ownership slot when the document processed but returned no
// owner/director rows, so the empty section reads as "add them yourself" rather
// than a silent failure.
function NoExtractionNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-2)',
        margin: 'var(--spacing-3) 0 0 0',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-label-sm)',
        color: 'var(--color-text-secondary)',
        lineHeight: 'var(--leading-normal)',
      }}
    >
      <AlertCircle size={15} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </p>
  )
}

function OwnershipUploadSlot({ state, file, task, onFile, onComplete, onError, onReset, fieldCount, requiredError = false }: OwnershipUploadSlotProps) {
  // Plain conditional render (no AnimatePresence mode="wait", which can stall
  // the idle→processing→done transition). A light fade-in per state is enough.
  if (state === 'processing' && file && task) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
        <ProcessingCard filename={file.name} task={task} onComplete={onComplete} onError={onError} />
      </motion.div>
    )
  }
  if (state === 'done' && file) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
        <DocumentCard file={file} fieldCount={fieldCount} onReplace={onFile} onRemove={onReset} />
      </motion.div>
    )
  }
  return (
    <div>
      <UploadZone
        onFileAccepted={onFile}
        subtext={UPLOAD_SUBTEXT}
        required_error={requiredError || state === 'error'}
      />
      {state === 'error' && (
        <p
          role="alert"
          style={{
            margin: 'var(--spacing-2) 0 0 var(--spacing-3)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            color: 'var(--color-error-text)',
          }}
        >
          {UPLOAD_ERROR_TEXT}
        </p>
      )}
      {requiredError && state !== 'error' && (
        <p
          role="alert"
          style={{
            margin: 'var(--spacing-2) 0 0 var(--spacing-3)',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            color: 'var(--color-error-text)',
          }}
        >
          Required: please upload to continue
        </p>
      )}
    </div>
  )
}

// Reassurance shown when the ownership structure was already filled in via the
// OTHER document route. Replaces "looks like nothing was uploaded" empty drop
// zones with a clear "you're done — upload below only to replace" note.
function RouteFilledNote({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-3)',
        background: 'var(--color-success-surface)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4) var(--spacing-6)',
        // 32px below the note, identical on both tabs.
        marginBottom: 'var(--spacing-8)',
      }}
    >
      <CheckCircle2
        size={20}
        aria-hidden="true"
        style={{ color: 'var(--color-success-text)', flexShrink: 0, marginTop: 2 }}
      />
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-sm)',
          color: 'var(--color-success-text)',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        {message}
      </p>
    </div>
  )
}

// Labelled wrapper for a slot inside the pair lane.
function LabelledSlot({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p
        style={{
          margin: '0 0 var(--spacing-2) 0',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-md)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
        }}
      >
        {label}
      </p>
      {children}
    </div>
  )
}

// ─── Main Screen ────────────────────────────────────────────────────────────

export function CompanyStructureScreen() {
  const navigate = useNavigate()
  const store = useFormStore()
  const { documents, refreshDocuments } = useSession()

  // Owners/directors persist as plain arrays in form_data so they survive
  // reloads and render on Review/History; local state stays the working copy.
  const [owners, setOwners] = useState<Owner[]>(
    () => (store.formData.owners as Owner[]) ?? []
  )

  // Which owner's passport / proof-of-address fields should play the fill
  // shimmer (set briefly when that upload completes, then cleared).
  const [passportShimmerId, setPassportShimmerId] = useState<string | null>(null)
  const [addressShimmerId, setAddressShimmerId] = useState<string | null>(null)

  const [directors, setDirectors] = useState<DirectorEntry[]>(
    () => ((store.formData.directors as LegacyDirectorEntry[] | undefined) ?? []).map(migrateDirectorEntry)
  )

  // Sync the working copies into form_data; the debounced auto-save PATCHes
  // them to the server (top-level jsonb keys are replaced wholesale). The refs
  // give the async extraction handlers a fresh read of the current lists
  // (without threading them through every setState) to decide import conflicts.
  const setFieldRef = useRef(store.setField)
  setFieldRef.current = store.setField
  const ownersRef = useRef(owners)
  const directorsRef = useRef(directors)
  useEffect(() => {
    ownersRef.current = owners
    setFieldRef.current('owners', owners)
  }, [owners])
  useEffect(() => {
    directorsRef.current = directors
    setFieldRef.current('directors', directors)
  }, [directors])

  // MAF 26-07: removing an ownership document clears the entries it filled. When
  // the user has ALSO added entries by hand (isAutoFilled === false), we ask
  // whether to delete everything or keep the manual ones, rather than silently
  // wiping their work. `slot` picks which reset runs on "Delete all".
  const [pendingDocRemoval, setPendingDocRemoval] = useState<{
    slot: 'incumbency' | 'shareholders' | 'directors'
    manualOwners: number
    manualDirectors: number
  } | null>(null)

  // Which director's passport-filled fields should play the fill shimmer.
  const [directorPassportShimmerId, setDirectorPassportShimmerId] = useState<string | null>(null)

  const [pendingRemoval, setPendingRemoval] = useState<{
    type: 'owner' | 'director'
    id: string
    name: string
  } | null>(null)

  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Collapsed owner/director cards — lets people close entries they've
  // already filled in to cut down scrolling once there are several of them.
  // New cards (added via the "Add" buttons) start open so they're ready to
  // fill in immediately.
  const [collapsedOwnerIds, setCollapsedOwnerIds] = useState<Set<string>>(new Set())
  const [collapsedDirectorIds, setCollapsedDirectorIds] = useState<Set<string>>(new Set())

  function toggleOwnerCollapsed(id: string) {
    setCollapsedOwnerIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleDirectorCollapsed(id: string) {
    setCollapsedDirectorIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Scroll targets for a blocked Continue: the upload section (when the cert is
  // still missing) and the owners section (e.g. ownership total ≠ 100).
  const uploadSectionRef = useRef<HTMLElement>(null)
  const uploadErrorRef = useRef<HTMLDivElement>(null)
  const ownersSectionRef = useRef<HTMLElement>(null)
  const directorsSectionRef = useRef<HTMLElement>(null)

  // ─── Certificate of Incumbency upload ────────────────────────────────────
  const [incumbencyState, setIncumbencyState] = useState<SlotState>('idle')
  const [incumbencyFile, setIncumbencyFile] = useState<UploadedFileInfo | null>(null)
  const [incumbencyTask, setIncumbencyTask] = useState<Promise<DocAIExtraction> | null>(null)

  function handleIncumbencyFile(file: File) {
    // Mutually-exclusive routes: uploading a Certificate of Incumbency replaces
    // anything from the two-certificate route, so the user never ends up with
    // documents sitting in both tabs.
    if (shareholdersState !== 'idle' || directorsState !== 'idle') {
      resetShareholders()
      resetDirectors()
    }
    setIncumbencyFile(file)
    setIncumbencyTask(processDocument(file, 'incumbency'))
    setIncumbencyState('processing')
    store.setUploadState('incumbency', 'processing')
  }

  // Apply an ownership document's extracted lists. The ownership document is
  // mandatory and is the evidence the declared structure is checked against, so
  // it's the source of truth: an extraction always replaces the corresponding
  // list, with no keep-vs-replace prompt. Letting a manually-entered list win
  // would leave the application contradicting its own supporting document.
  // Individual entries stay freely editable afterwards, so a misread extraction
  // can still be corrected by hand.
  function applyExtractedLists(extractedOwners: Owner[], extractedDirectors: DirectorEntry[]) {
    // An empty extraction is "nothing found", not "the document says none" —
    // it must not blank out a list.
    if (extractedOwners.length > 0) setOwners(extractedOwners)
    if (extractedDirectors.length > 0) setDirectors(extractedDirectors)
  }

  function handleIncumbencyComplete(result: DocAIExtraction) {
    applyExtractedLists(
      ownersFromExtraction(result.shareholders ?? []),
      directorsFromExtraction(result.directors ?? []),
    )
    setIncumbencyFieldCount(result.extractedFieldCount)
    setIncumbencyState('done')
    store.setUploadState('incumbency', 'success')
    // Sync session's document list so a later "Edit" back to this step (a
    // fresh mount) sees the upload already there instead of re-prompting.
    void refreshDocuments()
  }

  function handleIncumbencyError(err: unknown) {
    console.error('[docai] incumbency extraction failed', err)
    setIncumbencyState('error')
    setIncumbencyTask(null)
    store.setUploadState('incumbency', 'error')
  }

  // Clears the incumbency document itself (slot state), WITHOUT touching the
  // owner/director lists — the caller decides what happens to them.
  function clearIncumbencyDoc() {
    setIncumbencyState('idle')
    setIncumbencyFile(null)
    setIncumbencyTask(null)
    setIncumbencyFieldCount(null)
    store.setUploadState('incumbency', 'idle')
  }

  function resetIncumbency() {
    clearIncumbencyDoc()
    // MAF 29-05: removing the document erases everything it pre-filled and the
    // section re-locks (lists + address go inactive until a new upload).
    setOwners([])
    setDirectors([])
  }

  // ─── Ownership document route (MAF 29-05) ────────────────────────────────
  // The merchant either uploads ONE Certificate of Incumbency, or BOTH a
  // Certificate of Shareholders and a Certificate of Directors. They pick a
  // lane first; the form then shows one slot or two.
  const [ownershipLane, setOwnershipLane] = useState<'incumbency' | 'pair'>('incumbency')

  const [shareholdersState, setShareholdersState] = useState<SlotState>('idle')
  const [shareholdersFile, setShareholdersFile] = useState<UploadedFileInfo | null>(null)
  const [shareholdersTask, setShareholdersTask] = useState<Promise<DocAIExtraction> | null>(null)
  const [directorsState, setDirectorsState] = useState<SlotState>('idle')
  const [directorsFile, setDirectorsFile] = useState<UploadedFileInfo | null>(null)
  const [directorsTask, setDirectorsTask] = useState<Promise<DocAIExtraction> | null>(null)

  // Real extracted field/row count per ownership slot, shown on the document
  // card. null until an extraction completes (and after a reload, where the
  // count isn't known) so the card hides the "AI extracted …" line instead of
  // showing a made-up number.
  const [incumbencyFieldCount, setIncumbencyFieldCount] = useState<number | null>(null)
  const [shareholdersFieldCount, setShareholdersFieldCount] = useState<number | null>(null)
  const [directorsFieldCount, setDirectorsFieldCount] = useState<number | null>(null)

  // One-shot slot rehydration from server-persisted documents: after a reload
  // the upload slots show done with the stored filename (the extracted data —
  // owners/directors/address — already lives in form_data). If both routes
  // have live rows (possible after switching lanes without a server delete),
  // the incumbency route wins, matching the mutually-exclusive-lane rule.
  const rehydratedRef = useRef(false)
  useEffect(() => {
    if (rehydratedRef.current || documents.length === 0) return
    rehydratedRef.current = true
    const bySlot = (slot: string) => documents.find((d) => d.slot === slot)
    const incumbency = bySlot('incumbency')
    if (incumbency) {
      setIncumbencyFile((prev) => prev ?? { name: incumbency.filename, size: incumbency.size_bytes })
      setIncumbencyState((prev) => (prev === 'idle' ? 'done' : prev))
      return
    }
    const shareholders = bySlot('shareholders_certificate')
    const directorsCert = bySlot('directors_certificate')
    if (shareholders || directorsCert) setOwnershipLane('pair')
    if (shareholders) {
      setShareholdersFile((prev) => prev ?? { name: shareholders.filename, size: shareholders.size_bytes })
      setShareholdersState((prev) => (prev === 'idle' ? 'done' : prev))
    }
    if (directorsCert) {
      setDirectorsFile((prev) => prev ?? { name: directorsCert.filename, size: directorsCert.size_bytes })
      setDirectorsState((prev) => (prev === 'idle' ? 'done' : prev))
    }
  }, [documents])

  function handleShareholdersFile(file: File) {
    // Switching to the two-certificate route replaces a Certificate of Incumbency
    // if one was uploaded — only one route holds documents at a time.
    if (incumbencyState !== 'idle') resetIncumbency()
    setShareholdersFile(file)
    setShareholdersTask(processDocument(file, 'shareholders_certificate'))
    setShareholdersState('processing')
  }
  function handleShareholdersComplete(result: DocAIExtraction) {
    // The Shareholders certificate seeds the owners list. Registered office
    // address moved to Company Details (no longer sourced from this document).
    applyExtractedLists(ownersFromExtraction(result.shareholders ?? []), [])
    setShareholdersFieldCount(result.extractedFieldCount)
    setShareholdersState('done')
    void refreshDocuments()
  }
  function handleShareholdersError(err: unknown) {
    console.error('[docai] shareholders certificate extraction failed', err)
    setShareholdersState('error')
    setShareholdersTask(null)
  }
  // Clears the shareholders certificate itself (slot state), leaving the
  // owners list for the caller to decide on.
  function clearShareholdersDoc() {
    setShareholdersState('idle')
    setShareholdersFile(null)
    setShareholdersTask(null)
    setShareholdersFieldCount(null)
  }
  function resetShareholders() {
    clearShareholdersDoc()
    // The Shareholders certificate pre-filled the owners list.
    setOwners([])
  }

  function handleDirectorsFile(file: File) {
    // Same as the shareholders slot: committing to the two-certificate route
    // replaces a previously-uploaded Certificate of Incumbency.
    if (incumbencyState !== 'idle') resetIncumbency()
    setDirectorsFile(file)
    setDirectorsTask(processDocument(file, 'directors_certificate'))
    setDirectorsState('processing')
  }
  function handleDirectorsComplete(result: DocAIExtraction) {
    // The Directors certificate seeds the directors list.
    applyExtractedLists([], directorsFromExtraction(result.directors ?? []))
    setDirectorsFieldCount(result.extractedFieldCount)
    setDirectorsState('done')
    void refreshDocuments()
  }
  function handleDirectorsError(err: unknown) {
    console.error('[docai] directors certificate extraction failed', err)
    setDirectorsState('error')
    setDirectorsTask(null)
  }
  // Clears the directors certificate itself (slot state), leaving the directors
  // list for the caller to decide on.
  function clearDirectorsDoc() {
    setDirectorsState('idle')
    setDirectorsFile(null)
    setDirectorsTask(null)
    setDirectorsFieldCount(null)
  }
  function resetDirectors() {
    clearDirectorsDoc()
    // The Directors certificate pre-filled the directors list.
    setDirectors([])
  }

  // A document's Remove button routes through these: if the user has entries
  // they added by hand mixed in with the doc-filled ones, ask what to keep;
  // otherwise remove straight away (nothing manual to lose).
  function requestRemoveIncumbency() {
    const manualOwners = owners.filter((o) => !o.isAutoFilled).length
    const manualDirectors = directors.filter((d) => !d.isAutoFilled).length
    if (manualOwners > 0 || manualDirectors > 0) {
      setPendingDocRemoval({ slot: 'incumbency', manualOwners, manualDirectors })
    } else {
      resetIncumbency()
    }
  }
  function requestRemoveShareholders() {
    const manualOwners = owners.filter((o) => !o.isAutoFilled).length
    if (manualOwners > 0) {
      setPendingDocRemoval({ slot: 'shareholders', manualOwners, manualDirectors: 0 })
    } else {
      resetShareholders()
    }
  }
  function requestRemoveDirectors() {
    const manualDirectors = directors.filter((d) => !d.isAutoFilled).length
    if (manualDirectors > 0) {
      setPendingDocRemoval({ slot: 'directors', manualOwners: 0, manualDirectors })
    } else {
      resetDirectors()
    }
  }

  // "Delete all" — the original full reset for whichever slot is pending.
  function removalDeleteAll() {
    if (!pendingDocRemoval) return
    if (pendingDocRemoval.slot === 'incumbency') resetIncumbency()
    else if (pendingDocRemoval.slot === 'shareholders') resetShareholders()
    else resetDirectors()
    setPendingDocRemoval(null)
  }

  // "Keep the ones I added" — drop the document and only the entries it filled
  // (isAutoFilled), leaving the user's manual rows in place.
  function removalKeepManual() {
    if (!pendingDocRemoval) return
    const keepManualOwners = () => setOwners((prev) => prev.filter((o) => !o.isAutoFilled))
    const keepManualDirectors = () => setDirectors((prev) => prev.filter((d) => !d.isAutoFilled))
    if (pendingDocRemoval.slot === 'incumbency') {
      clearIncumbencyDoc()
      keepManualOwners()
      keepManualDirectors()
    } else if (pendingDocRemoval.slot === 'shareholders') {
      clearShareholdersDoc()
      keepManualOwners()
    } else {
      clearDirectorsDoc()
      keepManualDirectors()
    }
    setPendingDocRemoval(null)
  }

  const directorsWithLinks = useMemo(
    () =>
      directors.map((d) => ({
        ...d,
        linkedOwnerName: computeLinkedOwner(directorFullName(d), owners),
      })),
    [directors, owners]
  )

  const totalAllocated = owners.reduce((sum, o) => sum + (o.ownershipPct || 0), 0)

  // ─── Owner handlers ─────────────────────────────────────────────────────
  // MAF 26-05: drop the confidence key for a field once the user edits it —
  // the value is no longer the AI value.
  function dropConfidence(map: ConfidenceMap, field: string): ConfidenceMap {
    if (!(field in map)) return map
    const next = { ...map }
    delete next[field]
    return next
  }

  function handlePersonChange(id: string, field: keyof PersonOwner, value: string | number) {
    // Editing (or clearing) a field dismisses any visible validation errors;
    // they only return if the user tries to Continue again. See validation rule.
    setSubmitAttempted(false)
    setOwners((prev) =>
      prev.map((o) =>
        o.id === id && o.kind === 'person'
          ? {
              ...o,
              [field]: field === 'ownershipPct' ? Number(value) : value,
              confidenceByField: dropConfidence(o.confidenceByField, field),
            }
          : o
      )
    )
  }

  function handleHoldingChange(id: string, field: keyof HoldingOwner, value: string | number) {
    setSubmitAttempted(false)
    setOwners((prev) =>
      prev.map((o) =>
        o.id === id && o.kind === 'holding'
          ? {
              ...o,
              [field]: field === 'ownershipPct' ? Number(value) : value,
              confidenceByField: dropConfidence(o.confidenceByField, field),
            }
          : o
      )
    )
  }

  // Applies a passport extraction onto an entry, skipping fields the scan
  // didn't return (real passports regularly miss the passport number).
  // MAF 15.05: passport-derived values override Incumbency-derived ones
  // (Verified Decision #55). dob fills only when empty — it's a required
  // manual field the applicant may already have set.
  function applyPassport<T extends { dob: string; nationality: string; passportNumber: string; placeOfIssue: string; confidenceByField: ConfidenceMap }>(
    entry: T,
    result: DocAIExtraction
  ): T {
    const next = { ...entry, confidenceByField: { ...entry.confidenceByField } }
    const fill = (key: 'passportNumber' | 'placeOfIssue' | 'nationality' | 'dob', overwrite = true) => {
      const value = result.fields[key]
      if (value === undefined) return
      if (!overwrite && next[key]) return
      next[key] = value as T[typeof key]
      if (result.confidence[key] != null) next.confidenceByField[key] = result.confidence[key]
    }
    fill('passportNumber')
    fill('placeOfIssue')
    fill('nationality')
    fill('dob', false)
    return next
  }

  function handlePassportFill(id: string, result: DocAIExtraction) {
    setOwners((prev) =>
      prev.map((o) => (o.id === id && o.kind === 'person' ? applyPassport(o, result) : o))
    )
    // Play the fill sweep + mount the "AI-filled" pill on the scanned fields.
    setPassportShimmerId(id)
    setTimeout(() => setPassportShimmerId((curr) => (curr === id ? null : curr)), 2000)
    // Sync session's document list so a later "Edit" back to this step (a
    // fresh mount) sees the upload already there instead of re-prompting.
    void refreshDocuments()
  }

  // Delete the passport upload: clear the fields it filled and their AI marks.
  // The shared, required Nationality keeps its value (just loses the AI badge),
  // since blanking a required field on an optional-document delete is worse than
  // leaving a known value behind.
  function handlePassportClear(id: string) {
    setOwners((prev) =>
      prev.map((o) => {
        if (o.id !== id || o.kind !== 'person') return o
        const next = { ...o.confidenceByField }
        delete next.passportNumber
        delete next.placeOfIssue
        delete next.nationality
        return { ...o, passportNumber: '', placeOfIssue: '', confidenceByField: next }
      })
    )
    setPassportShimmerId((curr) => (curr === id ? null : curr))
  }

  function handleProofOfAddressFill(id: string, result: DocAIExtraction) {
    const address = result.fields.registeredAddress
    if (address === undefined) return
    setOwners((prev) =>
      prev.map((o) =>
        o.id === id && o.kind === 'person'
          ? {
              ...o,
              registeredAddress: address,
              confidenceByField:
                result.confidence.registeredAddress != null
                  ? { ...o.confidenceByField, registeredAddress: result.confidence.registeredAddress }
                  : o.confidenceByField,
            }
          : o
      )
    )
    setAddressShimmerId(id)
    setTimeout(() => setAddressShimmerId((curr) => (curr === id ? null : curr)), 2000)
    void refreshDocuments()
  }

  // Delete the proof-of-address upload: clear the address it filled + its mark.
  function handleProofOfAddressClear(id: string) {
    setOwners((prev) =>
      prev.map((o) => {
        if (o.id !== id || o.kind !== 'person') return o
        const next = { ...o.confidenceByField }
        delete next.registeredAddress
        return { ...o, registeredAddress: '', confidenceByField: next }
      })
    )
    setAddressShimmerId((curr) => (curr === id ? null : curr))
  }

  function handleAddOwner(kind: 'person' | 'holding') {
    setPickerOpen(false)
    if (kind === 'person') {
      setOwners((prev) => [
        ...prev,
        {
          kind: 'person',
          id: `owner-p-${Date.now()}`,
          firstName: '',
          lastName: '',
          ownershipPct: 0,
          nationality: '',
          dob: '',
          passportNumber: '',
          placeOfIssue: '',
          registeredAddress: '',
          isAutoFilled: false,
          confidenceByField: {},
        },
      ])
    } else {
      setOwners((prev) => [
        ...prev,
        {
          kind: 'holding',
          id: `owner-h-${Date.now()}`,
          legalCompanyName: '',
          ownershipPct: 0,
          countryOfIncorporation: '',
          registrationNumber: '',
          isAutoFilled: false,
          confidenceByField: {},
        },
      ])
    }
  }

  function handleRemoveOwner(id: string) {
    const o = owners.find((x) => x.id === id)
    if (!o) return
    const name =
      o.kind === 'person'
        ? personFullName(o) || 'this shareholder'
        : o.legalCompanyName || 'this holding company'
    setPendingRemoval({ type: 'owner', id, name })
  }

  function confirmRemoveOwner(id: string) {
    setOwners((prev) => prev.filter((o) => o.id !== id))
  }

  // ─── Director handlers ──────────────────────────────────────────────────
  function handleDirectorChange(id: string, field: string, value: string) {
    setSubmitAttempted(false)
    setDirectors((prev) =>
      prev.map((d) =>
        d.id === id
          ? { ...d, [field]: value, confidenceByField: dropConfidence(d.confidenceByField, field) }
          : d
      )
    )
  }

  // Passport scan fills passport number + place of issue and overrides
  // nationality (same as the shareholder card; Verified Decision #55 / MAF 15.05).
  function handleDirectorPassportFill(id: string, result: DocAIExtraction) {
    setDirectors((prev) =>
      prev.map((d) => (d.id === id ? applyPassport(d, result) : d))
    )
    setDirectorPassportShimmerId(id)
    setTimeout(() => setDirectorPassportShimmerId((curr) => (curr === id ? null : curr)), 2000)
    void refreshDocuments()
  }

  // Delete the passport upload: clear the fields it filled and their AI marks.
  // Nationality keeps its value (just loses the badge), like the shareholder card.
  function handleDirectorPassportClear(id: string) {
    setDirectors((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d
        const next = { ...d.confidenceByField }
        delete next.passportNumber
        delete next.placeOfIssue
        delete next.nationality
        return { ...d, passportNumber: '', placeOfIssue: '', confidenceByField: next }
      })
    )
    setDirectorPassportShimmerId((curr) => (curr === id ? null : curr))
  }

  function handleAddDirector() {
    setDirectors((prev) => [
      ...prev,
      {
        id: `director-${Date.now()}`,
        firstName: '',
        lastName: '',
        dob: '',
        nationality: '',
        passportNumber: '',
        placeOfIssue: '',
        isAutoFilled: false,
        isAuthorisedSignatory: false,
        confidenceByField: {},
      },
    ])
  }

  function handleRemoveDirector(id: string) {
    const d = directors.find((x) => x.id === id)
    const name = (d && directorFullName(d)) || 'this director'
    setPendingRemoval({ type: 'director', id, name })
  }

  function confirmRemoveDirector(id: string) {
    setDirectors((prev) => prev.filter((d) => d.id !== id))
  }

  function handleToggleSignatory(id: string, checked: boolean) {
    setDirectors((prev) =>
      prev.map((d) =>
        d.id === id
          ? { ...d, isAuthorisedSignatory: checked }
          : checked
            ? { ...d, isAuthorisedSignatory: false }
            : d
      )
    )
  }

  const signatoryCount = directors.filter((d) => d.isAuthorisedSignatory).length
  const showSignatoryHelper = signatoryCount > 0

  // ─── Validation ─────────────────────────────────────────────────────────
  function isPersonValid(o: PersonOwner): boolean {
    // Nationality and DoB are optional (product change) — not gated here.
    return !!(o.firstName.trim() && o.lastName.trim() && o.ownershipPct > 0)
  }
  function isHoldingValid(o: HoldingOwner): boolean {
    return !!(
      o.legalCompanyName.trim() &&
      o.ownershipPct > 0 &&
      o.countryOfIncorporation.trim() &&
      o.registrationNumber.trim()
    )
  }
  function isOwnerValid(o: Owner): boolean {
    return o.kind === 'person' ? isPersonValid(o) : isHoldingValid(o)
  }
  function isDirectorValid(d: DirectorEntry): boolean {
    // Nationality and DoB are optional (product change) — not gated here.
    return !!(d.firstName.trim() && d.lastName.trim())
  }
  const allOwnersValid = owners.every(isOwnerValid)
  const allDirectorsValid = directors.every(isDirectorValid)

  // A collapsed card still expands itself if a blocked Continue reveals a
  // validation error inside it, so red fields are never hidden.
  function isOwnerOpen(o: Owner): boolean {
    return !collapsedOwnerIds.has(o.id) || (submitAttempted && !isOwnerValid(o))
  }
  function isDirectorOpen(d: DirectorEntry): boolean {
    return !collapsedDirectorIds.has(d.id) || (submitAttempted && !isDirectorValid(d))
  }
  const ownershipOk = totalAllocated === 100
  const hasAnyOwner = owners.length > 0
  // MAF 29-05: the §3.0 ownership certificate is mandatory. The requirement is
  // met by EITHER the Incumbency certificate OR both the Shareholders and
  // Directors certificates. The address field, the owner/director lists and
  // their Add buttons stay locked until that requirement is satisfied.
  // The requirement is met by EITHER route, and — crucially — stays met when the
  // user switches tabs. Once the structure is mapped we don't re-lock the section
  // just because the other tab's upload slots happen to be empty.
  const filledViaIncumbency = incumbencyState === 'done'
  const filledViaPair = shareholdersState === 'done' && directorsState === 'done'
  const ownershipReady = filledViaIncumbency || filledViaPair
  const ownershipLocked = !ownershipReady

  // When the structure was filled on the OTHER tab, this tab's own slots are
  // still idle — show a reassurance note (with a replace option) instead of bare
  // drop zones that look like the upload was lost.
  const incumbencyFilledElsewhere =
    ownershipLane === 'incumbency' && incumbencyState === 'idle' && filledViaPair
  const pairFilledElsewhere =
    ownershipLane === 'pair' &&
    shareholdersState === 'idle' &&
    directorsState === 'idle' &&
    filledViaIncumbency
  const canContinue =
    ownershipReady &&
    ownershipOk &&
    allOwnersValid &&
    allDirectorsValid &&
    hasAnyOwner

  // The required ownership certificate is still missing → highlight the upload
  // zone(s) red. On the two-document lane, only flag whichever is still missing.
  const showOwnershipError = submitAttempted && ownershipLocked

  function handleContinue() {
    if (!canContinue) {
      setSubmitAttempted(true)
      // Top to bottom: the topmost field with a red border, else the missing-
      // document error box (falling back to the upload section), else the
      // owners section (e.g. ownership total ≠ 100).
      scrollToFirstError(
        FIRST_INVALID_FIELD,
        ownershipLocked ? (uploadErrorRef.current ?? uploadSectionRef.current) : ownersSectionRef.current
      )
      return
    }
    store.setStep(3)
    navigate('/processing-breakdown')
  }

  function handleConfirmRemoval() {
    if (!pendingRemoval) return
    const { type } = pendingRemoval
    if (type === 'owner') confirmRemoveOwner(pendingRemoval.id)
    else confirmRemoveDirector(pendingRemoval.id)
    setPendingRemoval(null)
    // After removing a card, bring the user up to that section's heading — where
    // the running total / counter (and any error) live — so the consequence of
    // the removal is visible instead of leaving them at the now-empty spot.
    scrollToFirstError((type === 'owner' ? ownersSectionRef : directorsSectionRef).current)
  }

  // Block-reason copy (rendered next to counter AND on/above Continue)
  function blockReason(): string | null {
    // MAF 29-05: the ownership certificate is mandatory. Surface that first, so
    // the user knows to upload before anything else can be completed. On the
    // two-certificate lane, name whichever document is still missing.
    if (ownershipLocked) {
      if (ownershipLane === 'incumbency') {
        return "Upload your Certificate of Incumbency above to continue. We'll fill in your owners and directors for you to review."
      }
      if (shareholdersState === 'done' && directorsState !== 'done') {
        return 'Certificate of Shareholders added. Please also upload the Certificate of Directors to continue.'
      }
      if (shareholdersState !== 'done' && directorsState === 'done') {
        return 'Certificate of Directors added. Please also upload the Certificate of Shareholders to continue.'
      }
      return 'Upload both the Certificate of Shareholders and the Certificate of Directors above to continue.'
    }
    if (!hasAnyOwner) {
      return 'Add at least one owner to continue.'
    }
    if (totalAllocated !== 100) {
      return `Ownership totals ${totalAllocated}%. It must equal exactly 100% before you can continue.`
    }
    if (!allOwnersValid || !allDirectorsValid) {
      return 'Please complete all the required fields for each owner and director.'
    }
    return null
  }

  // When the user tries to continue without the required ownership certificate,
  // show a prominent error box at the TOP of the upload section (alongside the
  // red drop zone) so the requirement is unmissable.
  //   • Once one half of the two-certificate route is in, name the missing half.
  //   • Otherwise (nothing uploaded yet), show the either/or so both routes are clear.
  const uploadErrorReason =
    shareholdersState === 'done' && directorsState !== 'done'
      ? 'Upload your Certificate of Directors to continue.'
      : directorsState === 'done' && shareholdersState !== 'done'
        ? 'Upload your Certificate of Shareholders to continue.'
        : 'A document is required to continue. Upload your Certificate of Incumbency, or both your Certificate of Shareholders and Directors.'

  const reason = blockReason()
  // While the certificate is still missing, the red upload zone (+ auto-scroll to
  // it) is the signal — repeating "upload above" here would be redundant. Only
  // surface the text reasons once unlocked, for things the box can't express
  // (ownership ≠ 100%, an incomplete owner/director, no owner added).
  const showBlockReason = submitAttempted && !!reason && !ownershipLocked

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <PageShell showStepper={true} currentStep={2}>
      {/* Page heading + intro */}
      <PageHeader title="Company Structure" intro="Define your ownership structure." />

      {/* ─── Certificate of Incumbency upload (required) ────────────────── */}
      <section
        ref={uploadSectionRef}
        style={{
          background: 'var(--color-surface-container-lowest)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--spacing-8)',
          // 48px below this card → extra breathing room above the Owners section.
          marginBottom: 'var(--spacing-12)',
        }}
      >
        <div style={{ marginBottom: 'var(--spacing-2)' }}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-md)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--color-text-primary)',
              margin: 0,
              lineHeight: 'var(--leading-tight)',
            }}
          >
            Certificate of Incumbency{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>or</span>{' '}
            Certificate of Shareholders &amp; Directors{' '}
            <Badge
              variant="required"
              size="md"
              style={{ verticalAlign: 'middle', marginLeft: 'var(--spacing-1)' }}
            >
              Required
            </Badge>
          </h2>
        </div>

        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-8) 0',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          Upload your certificate of incumbency, or your shareholder and director
          certificates, and our AI will map out your ownership structure below.
        </p>

        {/* Required-document error: shown on a blocked Continue when neither
            certificate route is satisfied yet. */}
        <AnimatePresence>
          {showOwnershipError && (
            <motion.div
              ref={uploadErrorRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              role="alert"
              style={{
                background: 'var(--color-error-surface)',
                color: 'var(--color-error-text)',
                border: '1px solid var(--color-error-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-4) var(--spacing-6)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-sm)',
                lineHeight: 'var(--leading-normal)',
                marginBottom: 'var(--spacing-6)',
              }}
            >
              {uploadErrorReason}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lane picker: choose which document route applies. */}
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-8)', flexWrap: 'wrap' }}>
          {([
            { id: 'incumbency', label: 'I have a Certificate of Incumbency' },
            { id: 'pair', label: 'I have a Certificate of Shareholders & Directors' },
          ] as const).map((opt) => {
            const active = ownershipLane === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setOwnershipLane(opt.id)}
                style={{
                  flex: '1 1 220px',
                  padding: 'var(--spacing-3) var(--spacing-4)',
                  border: '1px solid',
                  borderColor: active ? 'var(--color-action-primary)' : 'var(--color-outline-subtle)',
                  background: active ? 'var(--color-info-surface)' : 'var(--color-surface-container-lowest)',
                  color: active ? 'var(--color-action-primary)' : 'var(--color-text-secondary)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
                  fontSize: 'var(--text-label-md)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>

        {/* Reassurance note: rendered here (outside the lane block) so it sits in
            the same place with the same 32px gap below it on both tabs. */}
        {(incumbencyFilledElsewhere || pairFilledElsewhere) && (
          <RouteFilledNote
            message={
              ownershipLane === 'incumbency'
                ? "We've pre-filled your ownership structure from your Certificate of Shareholders & Directors. Have a Certificate of Incumbency instead? Upload it below to replace the current document."
                : 'Your ownership structure was already filled in from your Certificate of Incumbency. Prefer to use Shareholder & Director Certificates instead? Upload them below to replace it.'
            }
          />
        )}

        {ownershipLane === 'incumbency' ? (
          <LabelledSlot label="Certificate of Incumbency">
            <OwnershipUploadSlot
              state={incumbencyState}
              file={incumbencyFile}
              task={incumbencyTask}
              onFile={handleIncumbencyFile}
              onComplete={handleIncumbencyComplete}
              onError={handleIncumbencyError}
              onReset={requestRemoveIncumbency}
              fieldCount={incumbencyFieldCount ?? undefined}
              requiredError={showOwnershipError}
            />
            {incumbencyState === 'done' && owners.length === 0 && directors.length === 0 && (
              <NoExtractionNote>
                We couldn&rsquo;t read any shareholders or directors from this document. Add them manually below.
              </NoExtractionNote>
            )}
          </LabelledSlot>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-8)' }}>
            <LabelledSlot label="Certificate of Shareholders">
              <OwnershipUploadSlot
                state={shareholdersState}
                file={shareholdersFile}
                task={shareholdersTask}
                onFile={handleShareholdersFile}
                onComplete={handleShareholdersComplete}
                onError={handleShareholdersError}
                onReset={requestRemoveShareholders}
                fieldCount={shareholdersFieldCount ?? undefined}
                requiredError={showOwnershipError && shareholdersState !== 'done'}
              />
              {shareholdersState === 'done' && owners.length === 0 && (
                <NoExtractionNote>
                  We couldn&rsquo;t read any shareholders from this document. Add them manually below.
                </NoExtractionNote>
              )}
            </LabelledSlot>
            <LabelledSlot label="Certificate of Directors">
              <OwnershipUploadSlot
                state={directorsState}
                file={directorsFile}
                task={directorsTask}
                onFile={handleDirectorsFile}
                onComplete={handleDirectorsComplete}
                onError={handleDirectorsError}
                onReset={requestRemoveDirectors}
                fieldCount={directorsFieldCount ?? undefined}
                requiredError={showOwnershipError && directorsState !== 'done'}
              />
              {directorsState === 'done' && directors.length === 0 && (
                <NoExtractionNote>
                  We couldn&rsquo;t read any directors from this document. Add them manually below.
                </NoExtractionNote>
              )}
            </LabelledSlot>
          </div>
        )}
      </section>

      {/* ─── Unified ownership list ────────────────────────────────────── */}
      <section ref={ownersSectionRef} aria-labelledby="owners-heading" style={{ marginBottom: 'var(--spacing-12)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--spacing-4)',
            // 32px below the heading/counter row. When the error box is present
            // its -8px pull-up lands it on the design system's locked 24px gap;
            // with no error this collapses with the cards' top margin (also 32px).
            marginBottom: 'var(--spacing-8)',
          }}
        >
          {/* Let the heading/helper column flex and shrink so the counter stays
              pinned top-right even when its error-state text is longer (the
              helper wraps instead of bumping the counter onto its own line). */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              id="owners-heading"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-title-lg)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--color-text-primary)',
                margin: '0 0 var(--spacing-2) 0',
                lineHeight: 'var(--leading-tight)',
              }}
            >
              Shareholders
            </h2>
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-md)',
                color: 'var(--color-text-secondary)',
                margin: 0,
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {ownershipLocked
                ? 'Upload the certificate above to fill in your shareholders automatically.'
                : 'Add shareholders with more than 10% ownership'}
            </p>
          </div>

          {!ownershipLocked && (
            <div style={{ flexShrink: 0 }}>
              <OwnershipCounter
                allocated={totalAllocated}
                hasError={submitAttempted && totalAllocated !== 100}
              />
            </div>
          )}
        </div>

        {/* Block reason — canonical boxed alert below the heading/counter row */}
        {showBlockReason && reason && <SectionTopAlert>{reason}</SectionTopAlert>}

        {owners.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-4)' }}>
            <Button
              variant="tertiary"
              onClick={() =>
                setCollapsedOwnerIds((prev) =>
                  prev.size < owners.length ? new Set(owners.map((o) => o.id)) : new Set()
                )
              }
              style={{ fontSize: '0.8125rem' }}
            >
              {collapsedOwnerIds.size < owners.length ? 'Collapse all' : 'Expand all'}
            </Button>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // Per-card spacing lives on each card's marginBottom (bigger when the
            // card is open or is the last one, tighter between collapsed cards).
            // 32px below the heading/helper once cards are present; the tighter
            // 24px stays while the section is empty (just the disabled Add button).
            marginTop: owners.length > 0 ? 'var(--spacing-8)' : 'var(--spacing-6)',
          }}
        >
          <AnimatePresence initial={false}>
            {owners.map((owner, i) => {
              if (owner.kind === 'person') {
                const personIndex = owners.filter((o, j) => o.kind === 'person' && j <= i).length - 1
                return (
                  <ShareholderCard
                    key={owner.id}
                    owner={owner}
                    index={personIndex}
                    onChange={handlePersonChange}
                    onPassportFill={handlePassportFill}
                    onProofOfAddressFill={handleProofOfAddressFill}
                    onPassportClear={handlePassportClear}
                    onProofOfAddressClear={handleProofOfAddressClear}
                    passportShimmer={passportShimmerId === owner.id}
                    addressShimmer={addressShimmerId === owner.id}
                    onRemove={handleRemoveOwner}
                    showErrors={submitAttempted}
                    open={isOwnerOpen(owner)}
                    onToggleOpen={() => toggleOwnerCollapsed(owner.id)}
                    bottomGap={isOwnerOpen(owner) || i === owners.length - 1 ? 'var(--spacing-8)' : 'var(--spacing-4)'}
                    documents={documents}
                  />
                )
              }
              const holdingIndex = owners.filter((o, j) => o.kind === 'holding' && j <= i).length - 1
              return (
                <HoldingCard
                  key={owner.id}
                  owner={owner}
                  index={holdingIndex}
                  onChange={handleHoldingChange}
                  onRemove={handleRemoveOwner}
                  showErrors={submitAttempted}
                  open={isOwnerOpen(owner)}
                  onToggleOpen={() => toggleOwnerCollapsed(owner.id)}
                  bottomGap={isOwnerOpen(owner) || i === owners.length - 1 ? 'var(--spacing-8)' : 'var(--spacing-4)'}
                />
              )
            })}
          </AnimatePresence>

          <div>
            <Button
              variant="secondary"
              onClick={() => setPickerOpen(true)}
              disabled={ownershipLocked}
            >
              <Plus size={16} aria-hidden="true" />
              Add shareholder or holding company
            </Button>
          </div>
        </div>
      </section>

      {/* ─── Directors ────────────────────────────────────────────────── */}
      <section ref={directorsSectionRef} aria-labelledby="directors-heading" style={{ marginBottom: 'var(--spacing-10)' }}>
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <h2
            id="directors-heading"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-lg)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--color-text-primary)',
              margin: '0 0 var(--spacing-2) 0',
              lineHeight: 'var(--leading-tight)',
            }}
          >
            Company Directors
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-md)',
              color: 'var(--color-text-secondary)',
              margin: 0,
              lineHeight: 'var(--leading-normal)',
            }}
          >
            {directors.length > 0
              ? 'Review the directors identified from your document. You can add or remove directors as needed.'
              : 'Upload the certificate above to fill in your directors automatically. You can then add, edit, or remove them as needed.'}
          </p>
        </div>

        {directors.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-4)' }}>
            <Button
              variant="tertiary"
              onClick={() =>
                setCollapsedDirectorIds((prev) =>
                  prev.size < directors.length ? new Set(directors.map((d) => d.id)) : new Set()
                )
              }
              style={{ fontSize: '0.8125rem' }}
            >
              {collapsedDirectorIds.size < directors.length ? 'Collapse all' : 'Expand all'}
            </Button>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // Per-card spacing lives on each card's marginBottom (bigger when the
            // card is open or is the last one, tighter between collapsed cards).
            // 32px below the heading/helper once cards are present; the tighter
            // 24px stays while the section is empty (just the disabled Add button).
            marginTop: directors.length > 0 ? 'var(--spacing-8)' : 'var(--spacing-6)',
          }}
        >
          <AnimatePresence initial={false}>
            {directorsWithLinks.map((director, i) => (
              <DirectorCard
                key={director.id}
                director={director}
                index={i}
                canRemove={directors.length > 1}
                onChange={handleDirectorChange}
                onRemove={handleRemoveDirector}
                onToggleSignatory={handleToggleSignatory}
                showSignatoryHelper={showSignatoryHelper}
                onPassportFill={handleDirectorPassportFill}
                onPassportClear={handleDirectorPassportClear}
                passportShimmer={directorPassportShimmerId === director.id}
                showErrors={submitAttempted}
                open={isDirectorOpen(director)}
                onToggleOpen={() => toggleDirectorCollapsed(director.id)}
                bottomGap={isDirectorOpen(director) || i === directorsWithLinks.length - 1 ? 'var(--spacing-8)' : 'var(--spacing-4)'}
                documents={documents}
              />
            ))}
          </AnimatePresence>

          <div>
            <Button
              variant="secondary"
              onClick={handleAddDirector}
              disabled={ownershipLocked}
            >
              <Plus size={16} aria-hidden="true" />
              Add Director
            </Button>
          </div>
        </div>
      </section>

      {/* CTA. Block reasons live at the top of the card they relate to (the
          owners-section reason by the counter) — not stacked above the button. */}
      <div style={{ paddingBottom: 'var(--spacing-8)', marginTop: 'calc(-1 * var(--spacing-4))' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 'var(--spacing-4)',
            minHeight: '20px',
            marginBottom: 'var(--spacing-10)',
          }}
        >
          <p
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-sm)',
              color: 'var(--color-text-tertiary)',
              margin: 0,
            }}
          >
            Fields marked with{' '}
            <span style={{ color: 'var(--color-action-primary)' }}>*</span> are required.
          </p>
          <SaveIndicator state={store.saveState} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-3)' }}>
          <BackButton to="/company-details" />
          <Button
            variant="primary"
            onClick={handleContinue}
          >
            Continue to Processing Breakdown
          </Button>
          <StartOverButton inline />
        </div>
      </div>

      {/* Add-owner picker */}
      <AnimatePresence>
        {pickerOpen && <AddOwnerPicker onPick={handleAddOwner} onCancel={() => setPickerOpen(false)} />}
      </AnimatePresence>

      {/* Removal confirmation modal */}
      <AnimatePresence>
        {pendingRemoval && (
          <motion.div
            key="removal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'var(--color-scrim)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--spacing-6)',
              zIndex: 1000,
            }}
            onClick={() => setPendingRemoval(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--color-surface-container-lowest)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-float)',
                padding: 'var(--spacing-8)',
                maxWidth: 400,
                width: '100%',
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="removal-dialog-title"
            >
              <h3
                id="removal-dialog-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-title-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--color-text-primary)',
                  margin: '0 0 var(--spacing-2) 0',
                }}
              >
                Remove {pendingRemoval.type === 'owner' ? 'owner' : 'director'}?
              </h3>
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-body-sm)',
                  color: 'var(--color-text-secondary)',
                  margin: '0 0 var(--spacing-6) 0',
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                Are you sure you want to remove <strong>{pendingRemoval.name}</strong>? This will
                delete their details. You can add a new entry manually anytime.
              </p>
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={() => setPendingRemoval(null)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleConfirmRemoval}
                  style={{
                    height: '36px',
                    padding: '0 var(--spacing-4)',
                    fontSize: 'var(--text-body-sm)',
                    minWidth: 'unset',
                  }}
                >
                  Remove
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Document removal with manual entries mixed in: keep the ones the user
          added, or clear the whole list. */}
      {(() => {
        const r = pendingDocRemoval
        const manualTotal = (r?.manualOwners ?? 0) + (r?.manualDirectors ?? 0)
        const noun =
          r?.slot === 'shareholders'
            ? manualTotal === 1
              ? 'shareholder'
              : 'shareholders'
            : r?.slot === 'directors'
              ? manualTotal === 1
                ? 'director'
                : 'directors'
              : manualTotal === 1
                ? 'entry'
                : 'entries'
        return (
          <ConfirmDialog
            open={pendingDocRemoval !== null}
            tone="neutral"
            showClose
            icon={<Trash2 size={26} />}
            title="Remove this document?"
            description={
              r
                ? `Removing this document clears the entries it filled in. You added ${manualTotal} ${noun} by hand. Keep what you added, or delete them all?`
                : ''
            }
            secondaryLabel="Delete all entries"
            onSecondary={removalDeleteAll}
            confirmLabel="Keep my entries"
            onCancel={() => setPendingDocRemoval(null)}
            onConfirm={removalKeepManual}
          />
        )
      })()}
    </PageShell>
  )
}
